import { baseApi, PaginatedResponse } from 'sama-shared';
import type { AdminSettlement } from '../types/admin';
import type { Order } from '../types/order';
import type { GetSettlementsParams } from '../types/api';

export interface UpdateSettlementStatusDto {
  status: number;
}

export const settlementsApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['Settlement'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getSettlements: builder.query<PaginatedResponse<AdminSettlement>, GetSettlementsParams>({
        query: (params) => ({
          url: '/admin/settlement',
          method: 'GET',
          params,
        }),
        providesTags: (result) =>
          result?.data
            ? [
                ...result.data.map(({ id }) => ({ type: 'Settlement' as const, id })),
                { type: 'Settlement', id: 'LIST' },
              ]
            : [{ type: 'Settlement', id: 'LIST' }],
      }),

      getSettlementById: builder.query<AdminSettlement, { settlementId: string }>({
        query: ({ settlementId }) => ({
          url: `/admin/settlement/${settlementId}`,
          method: 'GET',
        }),
        providesTags: (_result, _error, { settlementId }) => [
          { type: 'Settlement', id: settlementId },
        ],
      }),

      getPendingSettlements: builder.query<AdminSettlement[], GetSettlementsParams>({
        query: (params) => ({
          url: '/admin/settlement/pending/all',
          method: 'GET',
          params,
        }),
        providesTags: (_result, _error, arg) => [
          { type: 'Settlement', id: arg.partnerId },
          { type: 'Settlement', id: 'LIST' },
        ],
      }),

      getOrderDetailById: builder.query<Order, { orderId: string }>({
        query: ({ orderId }) => ({
          url: `/admin/order/${orderId}`,
          method: 'GET',
        }),
        providesTags: (_result, _error, { orderId }) => [{ type: 'Settlement', id: orderId }],
      }),

      updateSettlementStatus: builder.mutation<
        AdminSettlement,
        { settlementId: string; body: UpdateSettlementStatusDto }
      >({
        query: ({ settlementId, body }) => ({
          url: `/admin/settlement/${settlementId}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (_result, _error, { settlementId }) => [
          { type: 'Settlement', id: settlementId },
          { type: 'Settlement', id: 'LIST' },
          'AdminOrder',
        ],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetSettlementsQuery,
  useGetSettlementByIdQuery,
  useGetPendingSettlementsQuery,
  useGetOrderDetailByIdQuery,
  useUpdateSettlementStatusMutation,
} = settlementsApi;
